import { useMemo, useState } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import { api, ApiError } from '@/lib/api';
import { useApiData } from '@/lib/useApi';
import type { ActivityRow } from '@/lib/types';
import { timeAgo, titleCase } from '@/lib/format';

const typeMeta: Record<string, { icon: string; color: string }> = {
  student: { icon: 'ri-graduation-cap-line', color: 'text-teal-600 bg-teal-50' },
  lead: { icon: 'ri-focus-3-line', color: 'text-amber-600 bg-amber-50' },
  application: { icon: 'ri-file-list-3-line', color: 'text-blue-600 bg-blue-50' },
  invoice: { icon: 'ri-bill-line', color: 'text-green-600 bg-green-50' },
  visa_case: { icon: 'ri-passport-line', color: 'text-purple-600 bg-purple-50' },
  note: { icon: 'ri-sticky-note-line', color: 'text-blue-600 bg-blue-50' },
  call: { icon: 'ri-phone-line', color: 'text-green-600 bg-green-50' },
  meeting: { icon: 'ri-team-line', color: 'text-amber-600 bg-amber-50' },
  document: { icon: 'ri-file-text-line', color: 'text-purple-600 bg-purple-50' },
};
const metaFor = (type: string) => typeMeta[type] || { icon: 'ri-history-line', color: 'text-gray-500 bg-gray-50' };

function detailOf(afterData: unknown): string {
  if (afterData && typeof afterData === 'object' && 'detail' in afterData) return String((afterData as { detail?: string }).detail || '');
  if (typeof afterData === 'string') { try { return String(JSON.parse(afterData)?.detail || ''); } catch { return ''; } }
  return '';
}

export default function ActivityPage() {
  const { data, loading, error, reload } = useApiData<ActivityRow[]>(() => api.activity());
  const [typeFilter, setTypeFilter] = useState('All');
  const [showLogModal, setShowLogModal] = useState(false);
  const [form, setForm] = useState({ type: 'note', action: '', detail: '' });
  const [saveErr, setSaveErr] = useState('');

  const logs = useMemo(() => data ?? [], [data]);
  const typeFilters = useMemo(() => ['All', ...Array.from(new Set(logs.map((l) => l.entityType)))], [logs]);
  const filtered = logs.filter((log) => typeFilter === 'All' || log.entityType === typeFilter);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaveErr('');
    try {
      await api.createActivity({ type: form.type, action: form.action, detail: form.detail });
      setShowLogModal(false); setForm({ type: 'note', action: '', detail: '' }); reload();
    } catch (cause) { setSaveErr(cause instanceof ApiError ? cause.message : 'Could not log activity'); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-gray-500">Filter by type:</span>
            <div className="flex items-center gap-1 flex-wrap">
              {typeFilters.map((f) => (
                <button key={f} onClick={() => setTypeFilter(f)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap ${typeFilter === f ? 'bg-[#0B6E77] text-white' : 'bg-white border border-[#E1E5EA] text-gray-500 hover:text-gray-700'}`}>{f === 'All' ? 'All' : titleCase(f)}</button>
              ))}
            </div>
          </div>
          <button onClick={() => { setShowLogModal(true); setSaveErr(''); }} className="flex items-center gap-2 px-4 py-2 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer whitespace-nowrap"><div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>Log Activity</button>
        </div>

        <div className="bg-white rounded-xl border border-[#E1E5EA] overflow-hidden">
          {loading ? <div className="p-10 text-center text-sm text-gray-400">Loading activity…</div>
            : error ? <div className="p-10 text-center text-sm text-red-600">{error}</div>
            : filtered.length === 0 ? <div className="p-12 text-center text-sm text-gray-400">No activity yet. Actions across the workspace will appear here.</div>
            : <div className="divide-y divide-gray-50">{filtered.map((log) => { const m = metaFor(log.entityType); const detail = detailOf(log.afterData); return (
              <div key={log.id} className="flex items-start gap-4 px-5 py-4 hover:bg-gray-50 transition-colors">
                <div className={`w-9 h-9 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5 ${m.color}`}><i className={`${m.icon} text-sm`} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-medium text-gray-800"><span className="text-[#3C3C3C]">{log.userName || 'System'}</span> <span className="font-normal text-gray-600">{log.action}</span></p>
                      {detail && <p className="text-xs text-gray-500 mt-0.5">{detail}</p>}
                    </div>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${m.color}`}>{titleCase(log.entityType)}</span>
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">{timeAgo(log.createdAt)}</p>
                </div>
              </div>
            ); })}</div>}
        </div>
      </div>

      {showLogModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/30" onClick={() => setShowLogModal(false)} />
          <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#E1E5EA]"><h3 className="text-sm font-semibold text-[#3C3C3C]">Log Activity</h3><button onClick={() => setShowLogModal(false)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer"><i className="ri-close-line text-gray-500 text-base" /></button></div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Activity Type</label><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77] bg-white cursor-pointer">{['note', 'call', 'meeting', 'document'].map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}</select></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Summary</label><input type="text" placeholder="e.g. Called Arjun Mehta about IELTS" value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77]" required /></div>
              <div><label className="block text-xs font-medium text-gray-600 mb-1">Details</label><textarea placeholder="Add more context..." value={form.detail} onChange={(e) => setForm({ ...form, detail: e.target.value })} className="w-full border border-[#E1E5EA] rounded-lg px-3 py-2 text-sm outline-none focus:border-[#0B6E77] resize-none" rows={3} maxLength={500} /></div>
              {saveErr && <p className="text-sm text-red-600">{saveErr}</p>}
              <button type="submit" className="w-full py-2.5 bg-[#0B6E77] text-white text-sm font-semibold rounded-lg hover:bg-[#085258] transition-colors cursor-pointer whitespace-nowrap">Save Activity</button>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
